import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../lib/firebase';
import { collection, getDocs, query, orderBy, getDoc, doc } from 'firebase/firestore';
import { TopNav } from '../components/TopNav';
import {
    TrendingUp, TrendingDown, Calendar, Users, BarChart2,
    Zap, ChevronRight, AlertTriangle, CheckCircle2, Clock,
    ArrowUpRight, Wallet, ShieldAlert, Activity, MessageCircle, QrCode
} from 'lucide-react';
import clsx from 'clsx';
import dayjs from 'dayjs';

// ── Helpers ───────────────────────────────────────────────────────
const fmt = (n) => `₪${Math.abs(n).toLocaleString('he-IL')}`;
const fmtSigned = (n) => `${n >= 0 ? '+' : '-'}₪${Math.abs(n).toLocaleString('he-IL')}`;

function StatCard({ label, value, sub, color = 'amber', icon: Icon, onClick, large }) {
    const colors = {
        amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
        emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
        rose: 'text-rose-400 bg-rose-500/10 border-rose-500/20',
        sky: 'text-sky-400 bg-sky-500/10 border-sky-500/20',
        indigo: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
        slate: 'text-slate-400 bg-slate-800 border-slate-700',
    };
    return (
        <div
            onClick={onClick}
            className={clsx(
                'bg-slate-900/70 border border-white/5 rounded-2xl p-4 flex flex-col gap-2 transition-all',
                onClick && 'cursor-pointer hover:border-white/10 active:scale-[0.98]'
            )}
        >
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-black text-slate-600 uppercase tracking-[0.25em] font-heebo">{label}</span>
                {Icon && (
                    <div className={clsx('w-7 h-7 rounded-xl border flex items-center justify-center flex-shrink-0', colors[color])}>
                        <Icon className="w-3.5 h-3.5" />
                    </div>
                )}
            </div>
            <div className={clsx('font-black tabular-nums leading-none font-heebo', large ? 'text-3xl' : 'text-2xl', colors[color].split(' ')[0])}>
                {value}
            </div>
            {sub && <div className="text-[10px] text-slate-600 font-heebo truncate">{sub}</div>}
        </div>
    );
}

function Skeleton() {
    return <div className="h-24 bg-slate-900/50 rounded-2xl animate-pulse" />;
}

// ── Main Component ─────────────────────────────────────────────────
export const HostDashboard = () => {
    const navigate = useNavigate();
    const hostId = auth.currentUser?.uid;

    const [loading, setLoading] = useState(true);
    const [hostName, setHostName] = useState('');
    const [stats, setStats] = useState(null);
    const [debtPlayers, setDebtPlayers] = useState([]);
    const [creditPlayers, setCreditPlayers] = useState([]);
    const [showDebtBreakdown, setShowDebtBreakdown] = useState(false);
    const [nextSession, setNextSession] = useState(null);
    const [activeSession, setActiveSession] = useState(null);
    const [tick, setTick] = useState(0);

    // 1-second ticker for countdowns
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (!hostId) return;
        loadData();
    }, [hostId]);

    const loadData = async () => {
        setLoading(true);
        try {
            // ── Host profile ───────────────────────────────────────
            const hostSnap = await getDoc(doc(db, `hosts/${hostId}`));
            if (hostSnap.exists()) setHostName(hostSnap.data().name || '');

            // ── All games ─────────────────────────────────────────
            const gSnap = await getDocs(
                query(collection(db, `hosts/${hostId}/games`), orderBy('startTime', 'desc'))
            );
            const games = gSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            const now = new Date();
            const startOfYear = new Date(now.getFullYear(), 0, 1).toISOString();

            const completed = games.filter(g => g.status === 'completed');
            const upcoming = games.filter(g => g.status === 'upcoming' || (!g.startedAt && new Date(g.startTime) > now));
            const active = games.find(g => g.status === 'active' || (g.startedAt && !g.completedAt));

            setActiveSession(active || null);

            const upcomingByDate = [...upcoming].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
            setNextSession(upcomingByDate[0] || null);

            // ── Year P&L: sum collected/expenses from session ledgers ─
            const yearCompleted = completed.filter(g => (g.completedAt || g.startTime) >= startOfYear);
            let yearIncome = 0;
            let yearExpenses = 0;

            // Also tally total sessions for avg players
            let totalPlayers = 0;
            let sessionCount = 0;

            // Fetch ledger data for year sessions (batch, max 20 for perf)
            const ledgerSessions = yearCompleted.slice(0, 50);
            await Promise.all(
                ledgerSessions.map(async (g) => {
                    // ledgerId / ledgerId2 stored on game doc
                    const ledgerIds = [g.ledgerId, g.ledgerId2].filter(Boolean);
                    for (const lid of ledgerIds) {
                        try {
                            const lSnap = await getDoc(doc(db, `hosts/${hostId}/sessions/${lid}`));
                            if (lSnap.exists()) {
                                const d = lSnap.data();
                                (d.collected || []).forEach(c => { yearIncome += Number(c.amount) || 0; });
                                (d.expenses || []).forEach(e => { yearExpenses += Number(e.amount) || 0; });
                            }
                        } catch {}
                    }
                    const players = (g.t1Players || 0) + (g.t2Players || 0);
                    if (players > 0) {
                        totalPlayers += players;
                        sessionCount++;
                    }
                })
            );

            const yearPnL = yearIncome - yearExpenses;
            const avgPlayers = sessionCount > 0 ? Math.round(totalPlayers / sessionCount) : 0;

            // ── Players: debt & credit breakdown ──────────────────
            const pSnap = await getDocs(collection(db, `hosts/${hostId}/players`));
            const players = pSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            // totalProfitLoss < 0 means player owes host (debt to host)
            // totalProfitLoss > 0 means player is up (host owes player = credit extended)
            const owed = players.filter(p => (p.totalProfitLoss || 0) < 0)
                .sort((a, b) => a.totalProfitLoss - b.totalProfitLoss);
            const credit = players.filter(p => (p.totalProfitLoss || 0) > 0)
                .sort((a, b) => b.totalProfitLoss - a.totalProfitLoss);

            const totalDebt = owed.reduce((s, p) => s + Math.abs(p.totalProfitLoss || 0), 0);
            const totalCredit = credit.reduce((s, p) => s + (p.totalProfitLoss || 0), 0);

            setDebtPlayers(owed);
            setCreditPlayers(credit);

            setStats({
                yearPnL,
                yearIncome,
                yearExpenses,
                completedCount: completed.length,
                completedYearCount: yearCompleted.length,
                upcomingCount: upcoming.length,
                communitySize: players.length,
                avgPlayers,
                totalDebt,
                totalCredit,
                debtCount: owed.length,
                creditCount: credit.length,
            });
        } catch (err) {
            console.error('[HostDashboard] load error:', err);
        } finally {
            setLoading(false);
        }
    };

    // ── Countdown helper ─────────────────────────────────────────
    const countdown = (isoTarget) => {
        const diff = Math.max(0, new Date(isoTarget) - Date.now());
        if (diff === 0) return 'עכשיו';
        const s = Math.floor(diff / 1000);
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        if (d > 0) return `${d}י ${h}ש`;
        if (h > 0) return `${h}ש ${String(m).padStart(2, '0')}ד`;
        return `${m}:${String(s % 60).padStart(2, '0')}`;
    };

    const greetHour = new Date().getHours();
    const greeting = greetHour < 12 ? 'בוקר טוב' : greetHour < 17 ? 'צהריים טובים' : 'ערב טוב';

    return (
        <div className="flex flex-col min-h-[100dvh] bg-slate-950 text-white" dir="rtl">
            <TopNav />

            <div className="w-full max-w-xl mx-auto px-4 py-6 space-y-5">

                {/* ── Greeting ── */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-black text-white font-heebo leading-none">
                            {greeting}{hostName ? `, ${hostName}` : ''} 👋
                        </h1>
                        <p className="text-xs text-slate-600 mt-1 font-heebo">
                            {dayjs().format('dddd, D MMMM YYYY')}
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/sessions')}
                        className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-black rounded-xl transition-colors font-heebo"
                    >
                        <Calendar className="w-3.5 h-3.5" />
                        ניהול סשנים
                    </button>
                </div>

                {/* ── WhatsApp Agent CTA ── */}
                <button
                    onClick={() => navigate('/whatsapp')}
                    className="w-full text-right rounded-2xl px-5 py-4 flex items-center justify-between transition-all active:scale-[0.99] group relative overflow-hidden border"
                    style={{
                        borderColor: 'rgba(18,165,102,0.35)',
                        backgroundImage: 'linear-gradient(135deg, rgba(18,165,102,0.14) 0%, rgba(11,15,26,0.6) 60%)',
                    }}
                >
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-2xl bg-felt-500/20 border border-felt-500/40 flex items-center justify-center flex-shrink-0 animate-pulseGlow">
                            <MessageCircle className="w-5 h-5 text-felt-400" />
                        </div>
                        <div>
                            <div className="text-sm font-black text-white font-heebo flex items-center gap-2">
                                סוכן ה‑WhatsApp
                                <span className="chip-live !py-0.5 !text-[9px]">חדש</span>
                            </div>
                            <div className="text-xs text-slate-400 font-heebo mt-0.5">
                                חברו את הוואטסאפ שלכם — שחקנים נרשמים, קונים כניסות ומתעדכנים בצ׳אט
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1 text-felt-400 flex-shrink-0">
                        <QrCode className="w-4 h-4" />
                        <ChevronRight className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    </div>
                </button>

                {/* ── Active Session Banner ── */}
                {activeSession && (
                    <button
                        onClick={() => navigate(`/session/${activeSession.id}`)}
                        className="w-full bg-emerald-500/10 border border-emerald-500/40 rounded-2xl px-5 py-4 flex items-center justify-between hover:border-emerald-500/70 transition-all active:scale-[0.99] group"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_10px_rgba(52,211,153,0.8)]" />
                            <div className="text-right">
                                <div className="text-xs font-black text-emerald-400 uppercase tracking-widest font-heebo">סשן פעיל עכשיו</div>
                                <div className="text-sm font-bold text-white font-heebo mt-0.5">
                                    {activeSession.stakes || activeSession.table1Config?.stakes} · {(activeSession.t1Players || 0) + (activeSession.t2Players || 0)} שחקנים
                                </div>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-emerald-500 group-hover:translate-x-[-2px] transition-transform" />
                    </button>
                )}

                {/* ── Next Session Card ── */}
                {nextSession && (
                    <div
                        onClick={() => navigate(`/session/${nextSession.id}`)}
                        className="bg-slate-900/70 border border-white/5 rounded-2xl p-4 cursor-pointer hover:border-amber-500/20 transition-all active:scale-[0.99] group"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[10px] font-black text-slate-600 uppercase tracking-[0.25em] font-heebo">הסשן הבא</span>
                            <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-amber-500 transition-colors" />
                        </div>
                        <div className="flex items-end justify-between">
                            <div>
                                <div className="text-xl font-black text-white font-heebo">
                                    {dayjs(nextSession.startTime).format('dddd, D MMM')}
                                </div>
                                <div className="text-sm text-slate-500 font-heebo mt-0.5">
                                    {dayjs(nextSession.startTime).format('HH:mm')} · {nextSession.stakes || nextSession.table1Config?.stakes || '—'}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-black text-amber-400 tabular-nums font-heebo leading-none" key={tick}>
                                    {countdown(nextSession.startTime)}
                                </div>
                                <div className="text-[10px] text-slate-600 uppercase tracking-widest font-heebo mt-1">עד הסשן</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Stats Grid ── */}
                {loading ? (
                    <div className="grid grid-cols-2 gap-3">
                        {[1,2,3,4,5,6].map(i => <Skeleton key={i} />)}
                    </div>
                ) : stats && (
                    <>
                        {/* Row 1: P&L + Outstanding Balance */}
                        <div className="grid grid-cols-2 gap-3">
                            <StatCard
                                label="רווח / הפסד שנתי"
                                value={fmtSigned(stats.yearPnL)}
                                sub={`הכנסות ${fmt(stats.yearIncome)} · הוצאות ${fmt(stats.yearExpenses)}`}
                                color={stats.yearPnL >= 0 ? 'emerald' : 'rose'}
                                icon={stats.yearPnL >= 0 ? TrendingUp : TrendingDown}
                                large
                            />
                            <StatCard
                                label="חובות פתוחים"
                                value={fmt(stats.totalDebt)}
                                sub={`${stats.debtCount} שחקנים חייבים`}
                                color={stats.totalDebt > 0 ? 'rose' : 'emerald'}
                                icon={Wallet}
                                onClick={() => setShowDebtBreakdown(v => !v)}
                                large
                            />
                        </div>

                        {/* Debt/Credit Breakdown (expandable) */}
                        {showDebtBreakdown && (
                            <div className="bg-slate-900/50 border border-white/5 rounded-2xl overflow-hidden">
                                {/* Owed to host */}
                                {debtPlayers.length > 0 && (
                                    <div>
                                        <div className="px-4 py-2 bg-rose-500/5 border-b border-white/5 flex items-center gap-2">
                                            <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />
                                            <span className="text-[10px] font-black text-rose-400 uppercase tracking-widest font-heebo">חייבים לך</span>
                                        </div>
                                        {debtPlayers.map(p => (
                                            <div
                                                key={p.id}
                                                onClick={() => navigate(`/player/${p.id}`)}
                                                className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/3 cursor-pointer transition-colors"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-[11px] font-black text-rose-400">
                                                        {(p.name || '?')[0].toUpperCase()}
                                                    </div>
                                                    <span className="text-sm font-bold text-white font-heebo">{p.name}</span>
                                                </div>
                                                <span className="text-sm font-black text-rose-400 tabular-nums font-heebo">
                                                    {fmt(Math.abs(p.totalProfitLoss))}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* Credit extended (host owes players) */}
                                {creditPlayers.length > 0 && (
                                    <div>
                                        <div className="px-4 py-2 bg-emerald-500/5 border-b border-white/5 flex items-center gap-2">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                                            <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest font-heebo">אתה חייב להם</span>
                                        </div>
                                        {creditPlayers.map(p => (
                                            <div
                                                key={p.id}
                                                onClick={() => navigate(`/player/${p.id}`)}
                                                className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/3 cursor-pointer transition-colors"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-[11px] font-black text-emerald-400">
                                                        {(p.name || '?')[0].toUpperCase()}
                                                    </div>
                                                    <span className="text-sm font-bold text-white font-heebo">{p.name}</span>
                                                </div>
                                                <span className="text-sm font-black text-emerald-400 tabular-nums font-heebo">
                                                    {fmt(p.totalProfitLoss)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {debtPlayers.length === 0 && creditPlayers.length === 0 && (
                                    <p className="text-center text-slate-600 text-xs py-6 font-heebo">אין יתרות פתוחות</p>
                                )}
                            </div>
                        )}

                        {/* Row 2: 3 smaller stats */}
                        <div className="grid grid-cols-3 gap-3">
                            <StatCard
                                label="סשנים מוזמנים"
                                value={stats.upcomingCount}
                                sub="מתוכנן"
                                color="amber"
                                icon={Calendar}
                                onClick={() => navigate('/sessions')}
                            />
                            <StatCard
                                label="סשנים השנה"
                                value={stats.completedYearCount}
                                sub={`${stats.completedCount} סה״כ`}
                                color="indigo"
                                icon={CheckCircle2}
                                onClick={() => navigate('/history')}
                            />
                            <StatCard
                                label="קהילה"
                                value={stats.communitySize}
                                sub="שחקנים רשומים"
                                color="sky"
                                icon={Users}
                                onClick={() => navigate('/community')}
                            />
                        </div>

                        {/* Row 3: 2 more stats */}
                        <div className="grid grid-cols-2 gap-3">
                            <StatCard
                                label="ממוצע שחקנים"
                                value={stats.avgPlayers || '—'}
                                sub="לסשן השנה"
                                color="slate"
                                icon={BarChart2}
                            />
                            <StatCard
                                label="יחס חוב/קרדיט"
                                value={stats.totalCredit > 0
                                    ? `${Math.round((stats.totalDebt / (stats.totalDebt + stats.totalCredit)) * 100)}%`
                                    : stats.totalDebt > 0 ? '100%' : '—'}
                                sub={`קרדיט ${fmt(stats.totalCredit)}`}
                                color={stats.totalDebt > stats.totalCredit ? 'rose' : 'emerald'}
                                icon={Activity}
                                onClick={() => setShowDebtBreakdown(v => !v)}
                            />
                        </div>
                    </>
                )}

                {/* ── Quick Nav ── */}
                <div className="grid grid-cols-2 gap-2 pt-1">
                    {[
                        { label: 'היסטוריית סשנים', path: '/history', icon: Clock, color: 'text-amber-500' },
                        { label: 'ניהול קהילה', path: '/community', icon: Users, color: 'text-sky-500' },
                        { label: 'ניהול אירוח', path: '/hospitality', icon: Zap, color: 'text-emerald-500' },
                        { label: 'מנהל רשימות', path: '/active-game', icon: ArrowUpRight, color: 'text-indigo-500' },
                    ].map(({ label, path, icon: Icon, color }) => (
                        <button
                            key={path}
                            onClick={() => navigate(path)}
                            className="flex items-center gap-3 bg-slate-900/50 border border-white/5 rounded-xl px-4 py-3 hover:border-white/10 transition-all active:scale-[0.98] group"
                        >
                            <Icon className={clsx('w-4 h-4 flex-shrink-0', color)} />
                            <span className="text-xs font-bold text-slate-400 group-hover:text-white transition-colors font-heebo">{label}</span>
                            <ChevronRight className="w-3.5 h-3.5 text-slate-700 mr-auto group-hover:text-slate-500 transition-colors" />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};
